import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { getSchoolConfig } from '../../utils/domainConfig';
import html2pdf from 'html2pdf.js';
import '../../styles/Receipt.css';
import { FaArrowLeft, FaDownload } from 'react-icons/fa';

const Receipt = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [transaction, setTransaction] = useState(null);
  const [student, setStudent] = useState(null);
  const [privateDetails, setPrivateDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const receiptRef = useRef();
  const schoolConfig = getSchoolConfig();
  
  const isLoggedIn = !!user;

  useEffect(() => {
    fetchReceiptData();
  }, [id]);

  const fetchReceiptData = async () => {
    try {
      setLoading(true);

      // Fetch transaction
      const { data: transactionData, error: transError } = await supabase
        .from('payment_transactions')
        .select('*')
        .eq('id', id)
        .single();

      if (transError) throw transError;

      // If logged in, fetch full student details from database
      if (isLoggedIn) {
        // Fetch student details
        const { data: studentData, error: studentError } = await supabase
          .from('students')
          .select('*')
          .eq('Rno', transactionData.student_rno)
          .single();

        if (studentError) throw studentError;

        // Fetch private details (father, mother, contact)
        const { data: privateData, error: privateError } = await supabase
          .from('students_private_details')
          .select('"Father\'s Name (Son)", "Mother\'s Name (Son)", Mobile1, Mobile2')
          .eq('Rno', transactionData.student_rno)
          .single();

        if (privateError) console.warn('Private details not found:', privateError);

        setTransaction(transactionData);
        setStudent(studentData);
        setPrivateDetails(privateData);
      } else {
        // If not logged in, get student info from URL parameters
        const searchParams = new URLSearchParams(location.search);
        const urlName = decodeURIComponent(searchParams.get('name') || '');
        const urlRno = decodeURIComponent(searchParams.get('rno') || '');
        const urlClass = decodeURIComponent(searchParams.get('class') || 'N/A');
        const urlFather = decodeURIComponent(searchParams.get('father') || 'N/A');
        const urlMother = decodeURIComponent(searchParams.get('mother') || 'N/A');

        // Create student object from URL params
        const studentFromUrl = {
          Name: urlName,
          Rno: urlRno,
          Class: urlClass
        };

        // Create private details object from URL params (without contact numbers)
        const privateDetailsFromUrl = {
          "Father's Name (Son)": urlFather,
          "Mother's Name (Son)": urlMother,
          Mobile1: null,
          Mobile2: null
        };

        setTransaction(transactionData);
        setStudent(studentFromUrl);
        setPrivateDetails(privateDetailsFromUrl);
      }
    } catch (error) {
      console.error('Error fetching receipt data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleDownloadPDF = () => {
    setIsDownloading(true);
    
    const element = receiptRef.current;
    const isMobile = window.innerWidth <= 768;
    
    const opt = {
      margin: isMobile ? [5, 5, 5, 5] : [10, 10, 10, 10],
      filename: `Receipt_${student.Name}_${transaction.id.substring(0, 8)}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: isMobile ? 1.5 : 2, 
        useCORS: true, 
        letterRendering: true 
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    html2pdf().set(opt).from(element).save().then(() => {
      setIsDownloading(false);
    }).catch((error) => {
      console.error('Error generating PDF:', error);
      setIsDownloading(false);
    });
  };



  if (loading) {
    return (
      <div className="receipt-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading receipt...</p>
        </div>
      </div>
    );
  }

  if (!transaction || !student) {
    return (
      <div className="receipt-container">
        <div className="error-message">
          <p>Receipt not found</p>
          {isLoggedIn && (
            <button onClick={() => navigate('/academic/transactions')}>
              Back to Transactions
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="receipt-container">
      {/* Action Buttons */}
      <div className="receipt-actions no-print">
        {isLoggedIn && (
          <button className="action-btn back-btn" onClick={() => navigate('/academic/transactions')}>
            <FaArrowLeft /> Back to Transactions
          </button>
        )}
        <button 
          className="action-btn download-btn" 
          onClick={handleDownloadPDF}
          disabled={isDownloading}
        >
          <FaDownload /> {isDownloading ? 'Generating PDF...' : 'Download PDF'}
        </button>
      </div>

      {/* Receipt */}
      <div className="receipt-paper" ref={receiptRef}>
        {/* School Header */}
        <div className="receipt-header">
          <div className="school-logo">
            <img src="/krm-logo.png" alt="KRM Logo" className="logo-image" />
          </div>
          <div className="school-info">
            <h1>{schoolConfig.schoolName}</h1>
            <p className="school-tagline">Excellence in Education</p>
            <div className="school-codes">
              <span>UDISE Code: 10051803224</span>
              <span className="separator">|</span>
              <span>Reg. No: 20710942024929180129</span>
            </div>
          </div>
        </div>

        <div className="receipt-title">
          <h2>FEE PAYMENT RECEIPT</h2>
          <div className="receipt-number">Receipt No: {transaction.id.toUpperCase()}</div>
        </div>

        {/* Receipt Details */}
        <div className="receipt-body">
          {/* Student Details Section */}
          <div className="section">
            <h3 className="section-title">Student Details</h3>
            <div className="details-grid">
              <div className="detail-row">
                <span className="detail-label">Student Name:</span>
                <span className="detail-value">{student.Name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Roll Number:</span>
                <span className="detail-value">{student.Rno}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Class:</span>
                <span className="detail-value">{student.Class}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Father's Name:</span>
                <span className="detail-value">{privateDetails?.["Father's Name (Son)"] || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Mother's Name:</span>
                <span className="detail-value">{privateDetails?.["Mother's Name (Son)"] || 'N/A'}</span>
              </div>
              {isLoggedIn && (
                <div className="detail-row">
                  <span className="detail-label">Contact:</span>
                  <span className="detail-value">
                    {privateDetails?.Mobile1 && privateDetails?.Mobile2 
                      ? `${privateDetails.Mobile1}, ${privateDetails.Mobile2}`
                      : privateDetails?.Mobile1 || privateDetails?.Mobile2 || 'N/A'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Payment Details Section */}
          <div className="section">
            <h3 className="section-title">Payment Details</h3>
            <div className="details-grid">
              <div className="detail-row">
                <span className="detail-label">Payment Date:</span>
                <span className="detail-value">{formatDate(transaction.created_at)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Payment Method:</span>
                <span className="detail-value payment-method">{transaction.payment_method}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Collected By:</span>
                <span className="detail-value">{transaction.created_by}</span>
              </div>
            </div>
          </div>

          {/* Fee Breakdown Section */}
          <div className="section fee-breakdown">
            <h3 className="section-title">Fee Breakdown</h3>
            <table className="fee-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="amount-col">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Previous Dues</td>
                  <td className={`amount-col ${
                    // If discount applied, show orange 'adjusted' class to indicate adjustment
                    transaction.discount && transaction.discount !== 0 
                      ? 'adjusted' 
                      : (transaction.fee_dues < 0 ? 'negative' : 'positive')
                  }`}>
                    {(() => {
                      // Always subtract discount from previous dues for display
                      const adjustedDues = (transaction.fee_dues || 0) - (transaction.discount || 0);
                      return <>{adjustedDues < 0 ? '−' : '+'} {Math.abs(adjustedDues)}</>;
                    })()}
                  </td>
                </tr>
                <tr>
                  <td>Monthly Fee</td>
                  <td className="amount-col">{transaction.current_month_fee || 0}</td>
                </tr>
                <tr>
                  <td>Transport Fee</td>
                  <td className="amount-col">{transaction.transport_fee || 0}</td>
                </tr>
                <tr>
                  <td>Examination Fee</td>
                  <td className="amount-col">{transaction.examination_fee || 0}</td>
                </tr>
                {transaction.other && transaction.other.length > 0 && (
                  <tr>
                    <td className="other-fee-cell">
                      {transaction.other.map((item, idx) => (
                        <span key={idx} className="other-fee-pill">{item.label}: ₹{Math.abs(item.value)}</span>
                      ))}
                    </td>
                    <td className="amount-col">
                      {transaction.other.reduce((sum, item) => sum + Math.abs(item.value || 0), 0)}
                    </td>
                  </tr>
                )}
                <tr className="total-row">
                  <td><strong>Total Amount Due</strong></td>
                  <td className="amount-col"><strong>{transaction.total_amount || 0}</strong></td>
                </tr>
                <tr className="paid-row">
                  <td><strong>Amount Paid</strong></td>
                  <td className="amount-col paid-amount"><strong>{transaction.amount_paid || 0}</strong></td>
                </tr>
                <tr className="balance-row">
                  <td><strong>Balance/Dues</strong></td>
                  <td className={`amount-col ${transaction.new_dues < 0 ? 'negative' : transaction.new_dues > 0 ? 'positive' : 'zero'}`}>
                    <strong>
                      {transaction.new_dues < 0 ? '−' : transaction.new_dues > 0 ? '+' : ''} {Math.abs(transaction.new_dues || 0)}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Receipt Footer */}
        <div className="receipt-footer">
          <div className="signature-section">
            <div className="signature-box">
              <div className="signature-line"></div>
              <p>Authorized Signature</p>
            </div>
            <div className="signature-box">
              <div className="signature-line"></div>
              <p>Parent/Guardian Signature</p>
            </div>
          </div>
          <div className="footer-note">
            <p>This is a computer-generated receipt and requires physical signature for acceptance</p>
            <p className="contact-info">For queries, contact: {schoolConfig.email} | {schoolConfig.phones.map(p => `+91${p}`).join(' | ')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Receipt;